import { useState, useEffect } from "react";
import { appsAPI } from "../../services/api";
import DataTable from "../../components/DataTable/DataTable";
import Modal from "../../components/Modal/Modal";
import AppForm from "./AppForm";
import { IApp } from "../../types";
import { useToast } from "../../hooks/useToast";
import "../Apps/Apps.css";

const AppsList = () => {
  const { showToast } = useToast();
  const [apps, setApps] = useState<IApp[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState<IApp | null>(null);
  const [filters, setFilters] = useState({
    published: "all", // all, published, draft
    includeDeleted: false,
  });

  useEffect(() => {
    fetchApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchApps = async () => {
    try {
      setIsLoading(true);
      const response = await appsAPI.getAllAdmin();

      let filteredApps = response.data.data || response.data || [];

      // Apply filters
      if (!filters.includeDeleted) {
        filteredApps = filteredApps.filter((app: IApp) => !app.isDeleted);
      }

      if (filters.published !== "all") {
        filteredApps = filteredApps.filter(
          (app: IApp) => app.published === (filters.published === "published")
        );
      }

      setApps(filteredApps);
    } catch (error: any) {
      console.error("Failed to fetch apps:", error);
      showToast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to fetch apps",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedApp(null);
    setShowModal(true);
  };

  const handleEdit = (app: IApp) => {
    setSelectedApp(app);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this app?")) return;

    try {
      await appsAPI.delete(id);
      showToast({
        type: "success",
        title: "Success",
        message: "App deleted successfully",
      });
      await fetchApps();
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Delete failed",
        message: error.message || "Failed to delete app",
      });
    }
  };

  const handleRestore = async (id: string) => {
    if (!window.confirm("Are you sure you want to restore this app?")) return;

    try {
      await appsAPI.restore(id);
      showToast({
        type: "success",
        title: "Success",
        message: "App restored successfully",
      });
      await fetchApps();
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to restore app",
      });
    }
  };

  const handlePermanentDelete = async (id: string) => {
    if (
      !window.confirm(
        "Are you sure you want to PERMANENTLY delete this app? This action cannot be undone!"
      )
    )
      return;

    try {
      await appsAPI.permanentDelete(id);
      showToast({
        type: "success",
        title: "Success",
        message: "App permanently deleted",
      });
      await fetchApps();
    } catch (error: any) {
      showToast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to permanently delete app",
      });
    }
  };

  const handleSuccess = () => {
    setShowModal(false);
    showToast({
      type: "success",
      title: "Success",
      message: selectedApp
        ? "App updated successfully"
        : "App created successfully",
    });
    fetchApps();
  };

  const handleFormSubmit = async (formData: FormData) => {
    try {
      if (selectedApp) {
        await appsAPI.update(selectedApp._id!, formData);
      } else {
        await appsAPI.create(formData);
      }
      handleSuccess();
    } catch (error: any) {
      console.error("Failed to save app:", error);
      showToast({
        type: "error",
        title: "Save failed",
        message: error.message || "Failed to save app",
      });
    }
  };

  const handleFormCancel = () => {
    setShowModal(false);
    setSelectedApp(null);
  };

  const columns = [
    {
      key: "image",
      label: "Image",
      render: (app: IApp) => (
        <div className="table-image-container">
          <img
            src={app.image}
            alt={app.name}
            className="table-image"
            onError={(e) => {
              (
                e.target as HTMLImageElement
              ).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                app.name
              )}&size=200&background=FF9933&color=fff&bold=true`;
            }}
          />
          {app.isDeleted && (
            <div className="deleted-overlay">
              <span>DELETED</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "name",
      label: "Name",
      render: (app: IApp) => (
        <div style={{ maxWidth: "250px" }}>
          <div
            style={{
              fontWeight: 600,
              marginBottom: "0.25rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
            }}
          >
            <span>{app.name}</span>
            {app.isDeleted && (
              <span
                style={{
                  padding: "0.25rem 0.75rem",
                  background: "#ef4444",
                  color: "white",
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                  borderRadius: "50px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Deleted
              </span>
            )}
          </div>
        </div>
      ),
    },
    { key: "category", label: "Category" },
    {
      key: "published",
      label: "Status",
      render: (app: IApp) => (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}
        >
          <span
            className={`status-badge ${app.published ? "published" : "draft"}`}
          >
            {app.published ? "Published" : "Draft"}
          </span>
          {app.isDeleted && (
            <span className="status-badge deleted-badge">🗑️ Deleted</span>
          )}
        </div>
      ),
    },
    {
      key: "featured",
      label: "Featured",
      render: (app: IApp) => (
        <span className="featured-badge">{app.featured ? "⭐" : "-"}</span>
      ),
    },
  ];

  // Custom actions for apps
  const getCustomActions = (app: IApp) => {
    if (app.isDeleted) {
      return (
        <>
          <button
            onClick={() => handleRestore(app._id!)}
            className="action-btn restore-btn"
            title="Restore"
          >
            ↩️ Restore
          </button>
          <button
            onClick={() => handlePermanentDelete(app._id!)}
            className="action-btn danger-btn"
            title="Permanently Delete"
          >
            🗑️ Delete Forever
          </button>
        </>
      );
    }
    return null;
  };

  return (
    <div className="apps-page">
      <div className="page-header">
        <div>
          <h1>Apps Management</h1>
          <p>Manage your applications and products ({apps.length} total)</p>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          <span>➕</span>
          <span>Add New App</span>
        </button>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.published}
            onChange={(e) =>
              setFilters({ ...filters, published: e.target.value })
            }
          >
            <option value="all">All</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={filters.includeDeleted}
              onChange={(e) =>
                setFilters({ ...filters, includeDeleted: e.target.checked })
              }
            />
            <span>Show Deleted</span>
          </label>
        </div>
      </div>

      <DataTable
        data={apps}
        columns={columns}
        isLoading={isLoading}
        onEdit={(row: IApp) => {
          if (!row.isDeleted) {
            handleEdit(row);
          }
        }}
        onDelete={(id: string) => {
          const app = apps.find((a) => a._id === id);
          if (app && !app.isDeleted) {
            handleDelete(id);
          }
        }}
        customActions={getCustomActions}
        emptyMessage="No apps found. Create your first app!"
      />

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={selectedApp ? "Edit App" : "Create New App"}
        >
          <AppForm
            app={selectedApp}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </Modal>
      )}

      <style>{`
        .table-image-container {
          position: relative;
          display: inline-block;
        }

        .deleted-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(239, 68, 68, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: white;
          font-size: 0.75rem;
          letter-spacing: 1px;
        }

        .deleted-badge {
          background: #ef4444 !important;
          color: white !important;
          font-size: 0.75rem;
          padding: 0.35rem 0.75rem;
        }

        .restore-btn {
          background: #10b981;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .restore-btn:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .danger-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .danger-btn:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          padding: 0.5rem 0;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default AppsList;
